import axios from 'axios';

const API_BASE_URL = '/api';

class AuthService {
  constructor() {
    this.token = localStorage.getItem('access_token');
    this.user = JSON.parse(localStorage.getItem('user') || 'null');
    
    // IMPORTANT: Track if we're on the login page to prevent any side effects
    this.isLoginPage = () => window.location.pathname === '/login';
    
    // Set up axios interceptor for adding token to requests
    axios.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Set up axios interceptor for handling 401 responses
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        console.log('[AuthService] Response error:', {
          url: error.config?.url,
          status: error.response?.status,
          isLoginRequest: error.config?.url?.includes('/auth/login')
        });
        
        // CRITICAL: Never trigger any navigation or state changes for login requests
        const isLoginRequest = error.config?.url?.includes('/auth/login') || 
                              error.config?.url?.includes('/auth/register');
        
        if (error.response?.status === 401 && !isLoginRequest && !this.isLoginPage()) {
          // Only handle 401 for non-login requests when not on login page
          console.log('[AuthService] Session expired, clearing auth data');
          this.clearAuthData();
        }
        
        return Promise.reject(error);
      }
    );
  }

  clearAuthData() {
    this.token = null;
    this.user = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
  }

  async login(email, password) {
    console.log('[AuthService] Login attempt started');
    console.log('[AuthService] Current URL:', window.location.href);
    
    // Clear any existing auth data BEFORE making the request
    this.clearAuthData();
    
    try {
      console.log('[AuthService] Making login request...');
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email,
        password
      });

      const { access_token, user } = response.data;
      
      // Check if user has access to web backend (admin or backoffice roles)
      if (user.role !== 'admin' && user.role !== 'backoffice') {
        console.log('[AuthService] Access denied for role:', user.role);
        this.clearAuthData();
        return { 
          success: false, 
          error: 'Zugriff verweigert. Sie haben keine Berechtigung für das Web-Backend. Bitte nutzen Sie die Tablet-Ansicht.' 
        };
      }
      
      this.token = access_token;
      this.user = user;
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      
      console.log('[AuthService] Login successful');
      return { success: true, user };
    } catch (error) {
      console.log('[AuthService] Login failed:', error.response?.status, error.response?.data);
      console.log('[AuthService] After failed login - Current URL:', window.location.href);
      
      // IMPORTANT: Just return the error, don't trigger any side effects
      const message = error.response?.data?.detail || 'Login failed';
      console.log('[AuthService] Returning error without side effects');
      return { success: false, error: message };
    }
  }

  async logout() {
    console.log('[AuthService] Logout');
    try {
      if (this.token) {
        await axios.post(`${API_BASE_URL}/auth/logout`);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearAuthData();
    }
  }

  async getCurrentUser() {
    console.log('[AuthService] getCurrentUser called');
    
    // CRITICAL: Don't make this call if we're on the login page
    if (this.isLoginPage()) {
      console.log('[AuthService] Skipping getCurrentUser - on login page');
      throw new Error('Cannot get current user on login page');
    }
    
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`);
      this.user = response.data;
      localStorage.setItem('user', JSON.stringify(this.user));
      console.log('[AuthService] getCurrentUser success:', this.user);
      return this.user;
    } catch (error) {
      console.log('[AuthService] getCurrentUser failed');
      // Just clear data without side effects
      this.clearAuthData();
      throw error;
    }
  }

  isAuthenticated() {
    return !!this.token && !!this.user;
  }

  getUser() {
    return this.user;
  }

  getToken() {
    return this.token;
  }
}

export default new AuthService();